import type { GraphLayer } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Chip, EmptyState, ErrorState, Icon, Skeleton, Table, TableSkeletonRows } from "@sagot/ui";
import { usePermissions } from "../../session/permissions";
import { OFF_CTA, type OffCta } from "./graph-model";
import type { GraphSummary } from "./graph-types";
import type { GraphAccess } from "./use-graph-access";
import { useGraphLinks } from "./use-graph-links";

/** The page while its first answer is on the way: the same blocks as the loaded page, at their real sizes. */
export function GraphPageSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-busy="true" className="space-y-6" data-testid="graph-skeleton">
      <span className="sr-only">{t("knowledgeGraph.loading")}</span>
      <section aria-hidden="true" className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="card card-body space-y-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-28" />
          </div>
        ))}
      </section>
      <section aria-hidden="true" className="grid gap-4 lg:grid-cols-2">
        <div className="card card-body space-y-3">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-full max-w-sm" />
        </div>
        <div className="card card-body space-y-3">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-2 w-full" />
        </div>
      </section>
      <div className="card overflow-hidden" aria-hidden="true">
        <div className="flex flex-col gap-3 border-b border-line p-4 lg:flex-row">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-full lg:w-48" />
          <Skeleton className="h-10 w-full lg:w-48" />
        </div>
        <ListSkeleton />
      </div>
    </div>
  );
}

/** Rows of the item list while a page loads: table rows on desktop, cards on a phone. */
export function ListSkeleton() {
  return (
    <>
      <Table aria-hidden="true" containerClassName="hidden md:block">
        <tbody>
          <TableSkeletonRows rows={6} columns={5} />
        </tbody>
      </Table>
      <ul aria-hidden="true" className="divide-y divide-line md:hidden">
        {Array.from({ length: 4 }, (_, index) => (
          <li key={index} className="space-y-2 p-4">
            <Skeleton className="h-4 w-48 max-w-full" />
            <Skeleton className="h-3 w-24" />
          </li>
        ))}
      </ul>
    </>
  );
}

/** The summary could not be read: friendly text and a retry; nothing else on the page can be trusted yet. */
export function GraphLoadError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="card" data-testid="graph-error">
      <ErrorState title={t("knowledgeGraph.error.title")} description={t("knowledgeGraph.error.body")} retryLabel={t("knowledgeGraph.error.retry")} onRetry={onRetry} />
    </div>
  );
}

/** Why the controls are off, in words (not only in a tooltip): a read-only role or support session. */
export function AccessNotice({ access }: { access: GraphAccess }) {
  const { t } = useTranslation();
  if (access.isPending || (access.canEdit && access.canRebuild)) return null;
  const key = access.editLock === "support" || access.rebuildLock === "support" ? "support" : access.canEdit ? "rebuildOnly" : "viewOnly";
  return (
    <div role="status" data-testid="graph-access-notice" className="flex items-start gap-3 rounded-md bg-sunken px-4 py-3 text-sm text-fg-muted">
      <Icon name="lock" className="mt-0.5 size-4 shrink-0" />
      <span>{t(`knowledgeGraph.access.${key}`)}</span>
    </div>
  );
}

function LayerRow({ on, label }: { on: boolean; label: string }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-2 text-sm">
      <Icon name={on ? "check-circle" : "x-circle"} className={on ? "size-4 shrink-0 text-success" : "size-4 shrink-0 text-fg-subtle"} />
      <span className="min-w-0 flex-1">{label}</span>
      <span className="text-xs font-semibold text-fg-muted">{on ? t("knowledgeGraph.off.on") : t("knowledgeGraph.off.off")}</span>
    </li>
  );
}

/** The four layers that decide whether the graph runs, in the order they are checked, each with its state. */
function LayerChecklist({ layers }: { layers: GraphSummary["layers"] }) {
  const { t } = useTranslation();
  const rows: readonly { layer: GraphLayer; on: boolean }[] = [
    { layer: "platform_flag", on: layers.platform_flag },
    { layer: "plan", on: layers.plan_feature },
    { layer: "tenant_override", on: layers.tenant_switch !== "off" },
    { layer: "agent", on: layers.extraction_enabled },
  ];
  return (
    <ol aria-label={t("knowledgeGraph.off.layersLabel")} className="mx-auto w-full max-w-sm space-y-2 text-left">
      {rows.map((row) => (
        <LayerRow key={row.layer} on={row.on} label={t(`knowledgeGraph.off.layers.${row.layer}`)} />
      ))}
    </ol>
  );
}

function OffAction({ cta }: { cta: OffCta }) {
  const { t } = useTranslation();
  const links = useGraphLinks();
  const permissions = usePermissions();
  if (cta === "billing" && !permissions.can("billing.view")) return null;
  const href = cta === "billing" ? links.billing : cta === "agent" ? links.agentRetrieval : links.knowledge;
  return (
    <Button asChild variant={cta === "knowledge" ? "secondary" : "primary"}>
      <AppLink href={href}>
        <Icon name="arrow-right" />
        {t(`knowledgeGraph.off.${cta}Cta`)}
      </AppLink>
    </Button>
  );
}

/** The graph is off for the workspace: which layer says so, what that means, and the one way forward. */
export function GraphOffState({ layer, layers }: { layer: GraphLayer; layers: GraphSummary["layers"] }) {
  const { t } = useTranslation();
  return (
    <section className="card" data-testid="graph-off" data-layer={layer} aria-label={t("knowledgeGraph.off.regionLabel")}>
      <EmptyState icon="lock" title={t(`knowledgeGraph.off.${layer}.title`)} description={t(`knowledgeGraph.off.${layer}.body`)} action={<OffAction cta={OFF_CTA[layer]} />} />
      <div className="space-y-3 border-t border-line px-5 py-4">
        <p className="text-center">
          <Chip tone="outline" icon="info" data-testid="graph-blocked-by">
            {t("knowledgeGraph.off.blockedBy", { layer: t(`knowledgeGraph.off.layers.${layer}`) })}
          </Chip>
        </p>
        <LayerChecklist layers={layers} />
      </div>
    </section>
  );
}

/** The graph is allowed but no assistant reads new documents into it: say so, with the setting to change. */
export function NoAssistantNotice() {
  const { t } = useTranslation();
  const links = useGraphLinks();
  return (
    <div role="status" data-testid="graph-no-assistant" className="flex flex-wrap items-center gap-3 rounded-md bg-warning-soft px-4 py-3 text-sm text-warning">
      <Icon name="alert-triangle" className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">{t("knowledgeGraph.noAssistant.body")}</span>
      <Button asChild variant="secondary" size="sm">
        <AppLink href={links.agentRetrieval}>{t("knowledgeGraph.noAssistant.cta")}</AppLink>
      </Button>
    </div>
  );
}

interface EmptyGraphProps {
  canRebuild: boolean;
  extractionEnabled: boolean;
  onRebuild: () => void;
}

/** Allowed, but nothing has been read into the graph yet. */
export function GraphEmptyState({ canRebuild, extractionEnabled, onRebuild }: EmptyGraphProps) {
  const { t } = useTranslation();
  const links = useGraphLinks();
  const action =
    extractionEnabled && canRebuild ? (
      <Button onClick={onRebuild} data-testid="graph-empty-rebuild">
        <Icon name="refresh-cw" />
        {t("knowledgeGraph.rebuild.action")}
      </Button>
    ) : (
      <Button asChild variant="secondary">
        <AppLink href={extractionEnabled ? links.knowledge : links.agentRetrieval}>
          <Icon name="arrow-right" />
          {t(extractionEnabled ? "knowledgeGraph.off.knowledgeCta" : "knowledgeGraph.noAssistant.cta")}
        </AppLink>
      </Button>
    );
  return (
    <section className="card" data-testid="graph-empty" aria-label={t("knowledgeGraph.empty.regionLabel")}>
      <EmptyState
        icon="git-branch"
        title={t("knowledgeGraph.empty.title")}
        description={t(extractionEnabled ? "knowledgeGraph.empty.body" : "knowledgeGraph.empty.bodyNoAssistant")}
        action={action}
      />
    </section>
  );
}
